/**
 * Student Hub API Service
 * 
 * Features:
 * - Session caching by (category + level + q + filters)
 * - 300ms debounced search with AbortController
 * - Retry on failure
 * - Error handling
 *
 * iter300 — Use the smart `getApiBase()` resolver instead of reading
 * `process.env.REACT_APP_BACKEND_URL` directly. On the production custom
 * domain (wassla.tech) the build-time URL points at the preview backend;
 * resolving to same-origin avoids the cross-origin "Failed to fetch".
 */

import { getApiBase } from '../api/httpClient';

// API base is resolved lazily so it picks up the right value when called
// from a SPA route, not at module-eval time.
const apiUrl = (path) => `${getApiBase()}${path}`;

// Session cache keyed by request signature
const sessionCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Generate cache key from request params
const getCacheKey = (endpoint, params) => {
  const sorted = Object.entries(params || {})
    .filter(([_, v]) => v !== null && v !== undefined && v !== '')
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${v}`)
    .join('&');
  return `${endpoint}?${sorted}`;
};

// Check if cache entry is valid
const isCacheValid = (entry) => {
  return entry && (Date.now() - entry.timestamp) < CACHE_TTL;
};

// Get from cache
const getFromCache = (key) => {
  const entry = sessionCache.get(key);
  if (isCacheValid(entry)) {
    return entry.data;
  }
  sessionCache.delete(key);
  return null;
};

// Set to cache
const setToCache = (key, data) => {
  sessionCache.set(key, { data, timestamp: Date.now() });
};

// Clear cache for a category (useful after mutations)
export const clearCategoryCache = (category) => {
  for (const key of sessionCache.keys()) {
    if (key.includes(`category=${category}`)) {
      sessionCache.delete(key);
    }
  }
};

// Clear all cache
export const clearAllCache = () => {
  sessionCache.clear();
};

/**
 * Fetch Student Hub items with caching
 */
export const fetchStudentHubItems = async ({
  category,
  level,
  genre,
  q,
  type,
  mode,
  condition,
  locationType,
  isPaid,
  priceMin,
  priceMax,
  city,
  country,
  sort = 'newest',
  limit = 50,
  offset = 0,
  signal, // AbortController signal
  skipCache = false,
}) => {
  const params = { category, level, genre, q, type, mode, condition, locationType, isPaid, priceMin, priceMax, city, country, sort, limit, offset };
  const cacheKey = getCacheKey('/api/student-hub/items', params);
  
  // Check cache first
  if (!skipCache) {
    const cached = getFromCache(cacheKey);
    if (cached) {
      return { ...cached, fromCache: true };
    }
  }
  
  // Build query string
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      queryParams.append(key, value);
    }
  });
  
  const response = await fetch(apiUrl(`/api/student-hub/items?${queryParams}`), {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    signal,
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }
  
  const data = await response.json();
  
  // Cache the result
  setToCache(cacheKey, data);
  
  return { ...data, fromCache: false };
};

/**
 * Fetch CV templates
 */
export const fetchCVTemplates = async ({ level, tier, signal } = {}) => {
  const params = { level, tier };
  const cacheKey = getCacheKey('/api/student-hub/cv-templates', params);
  
  const cached = getFromCache(cacheKey);
  if (cached) {
    return { ...cached, fromCache: true };
  }
  
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      queryParams.append(key, value);
    }
  });
  
  const url = queryParams.toString() 
    ? apiUrl(`/api/student-hub/cv-templates?${queryParams}`)
    : apiUrl(`/api/student-hub/cv-templates`);
    
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    signal,
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }
  
  const data = await response.json();
  setToCache(cacheKey, data);
  
  return { ...data, fromCache: false };
};

/**
 * Get user preferences (academicLevel)
 */
export const fetchUserPrefs = async (token) => {
  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(apiUrl(`/api/user/prefs`), {
    method: 'GET',
    headers,
    credentials: 'include',
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }
  
  return response.json();
};

/**
 * Update user preferences (academicLevel)
 */
export const updateUserPrefs = async (token, prefs) => {
  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(apiUrl(`/api/user/prefs`), {
    method: 'PATCH',
    headers,
    credentials: 'include',
    body: JSON.stringify(prefs),
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }
  
  return response.json();
};

/**
 * Debounced search helper
 * Returns a function that debounces search calls
 */
export const createDebouncedSearch = (delay = 300) => {
  let timeoutId = null;
  let abortController = null;
  
  return (searchFn) => {
    // Clear previous timeout
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    
    // Abort previous request
    if (abortController) {
      abortController.abort();
    }
    
    // Create new abort controller
    abortController = new AbortController();
    const signal = abortController.signal;
    
    return new Promise((resolve, reject) => {
      timeoutId = setTimeout(async () => {
        try {
          const result = await searchFn(signal);
          resolve(result);
        } catch (error) {
          if (error.name === 'AbortError') {
            // Ignore abort errors
            resolve(null);
          } else {
            reject(error);
          }
        }
      }, delay);
    });
  };
};

/**
 * Category configuration
 */
export const CATEGORY_CONFIG = {
  'books_notes': {
    title: { en: 'Books', ar: 'كتب وملخصات' },
    searchHint: { en: 'Search books, notes...', ar: 'ابحث عن كتب، ملخصات...' },
    icon: '📚',
    route: '/student/books',
  },
  'internships_jobs': {
    title: { en: 'Internships & Jobs', ar: 'تدريب ووظائف' },
    searchHint: { en: 'Search internships, jobs...', ar: 'ابحث عن تدريب ووظائف...' },
    icon: '💼',
    route: '/student/internships',
  },
  'scholarships': {
    title: { en: 'Scholarships', ar: 'منح دراسية' },
    searchHint: { en: 'Search scholarships...', ar: 'ابحث عن منح دراسية...' },
    icon: '🎓',
    route: '/student/scholarships',
  },
  'universities_admissions': {
    title: { en: 'Universities & Admissions', ar: 'جامعات وتقديم' },
    searchHint: { en: 'Search universities...', ar: 'ابحث عن جامعات وتقديم...' },
    icon: '🏛️',
    route: '/student/admissions',
  },
  'courses_learning': {
    title: { en: 'Courses & Skills', ar: 'كورسات ومهارات' },
    searchHint: { en: 'Search courses...', ar: 'ابحث عن كورسات...' },
    icon: '🧠',
    route: '/student/courses',
  },
  'cv_cover': {
    title: { en: 'CV & Cover Letter', ar: 'السيرة الذاتية' },
    searchHint: { en: 'Search templates...', ar: 'ابحث عن قوالب...' },
    icon: '📝',
    route: '/student/cv',
  },
};

/**
 * Academic levels configuration
 */
export const ACADEMIC_LEVELS = [
  { key: 'secondary', label_en: 'Secondary', label_ar: 'ثانوي' },
  { key: 'university', label_en: 'University', label_ar: 'جامعي' },
  { key: 'postgrad', label_en: 'Postgrad', label_ar: 'دراسات عليا' },
  { key: 'vocational', label_en: 'Vocational', label_ar: 'مهني/تقني' },
  { key: 'other', label_en: 'Other', label_ar: 'أخرى' },
];

export default {
  fetchStudentHubItems,
  fetchCVTemplates,
  fetchUserPrefs,
  updateUserPrefs,
  createDebouncedSearch,
  clearCategoryCache,
  clearAllCache,
  CATEGORY_CONFIG,
  ACADEMIC_LEVELS,
};
